import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, ShieldCheck, CheckCircle2, XCircle, Mail, Clock } from 'lucide-react';

/**
 * AdminBetaApplicationsPanel
 * Admin-only panel for reviewing beta tester applications.
 *
 * Approve flow:
 *   - Auto-creates account with shared password (no email sent)
 *   - Flips beta_tester=true + is_unlimited=true
 *   - Returns the shared password so admin can DM it to applicant
 *
 * Reject flow:
 *   - Marks application rejected with optional reason
 *   - Does NOT create an account
 */
export default function AdminBetaApplicationsPanel({ apiUrl, token }) {
  const [apps, setApps] = useState([]);
  const [counts, setCounts] = useState({ pending_review: 0, approved: 0, rejected: 0 });
  const [filter, setFilter] = useState('pending_review');
  const [loading, setLoading] = useState(false);
  const [busyEmail, setBusyEmail] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${apiUrl}/api/admin/beta-applications${filter ? `?status=${filter}` : ''}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || r.statusText);
      setApps(d.applications || []);
      setCounts(d.counts || {});
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token, filter]);

  useEffect(() => { load(); }, [load]);

  const approve = async (email) => {
    if (!window.confirm(`Approve ${email}?\n\nThis will:\n• Auto-create their account (or upgrade existing)\n• Set beta_tester=true + is_unlimited=true\n• Return the shared password for you to DM them\n\nNo email is sent automatically.`)) return;
    setBusyEmail(email);
    try {
      const r = await fetch(`${apiUrl}/api/admin/beta-applications/${encodeURIComponent(email)}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || r.statusText);
      window.alert(
        `Approved ${d.email}\n\n` +
        `User ID: ${d.user_id}\n` +
        `Account created: ${d.account_created ? 'YES (new)' : 'NO (upgraded existing)'}\n\n` +
        `📋 DM these credentials to the applicant:\n` +
        `   Email: ${d.email}\n` +
        `   Password: ${d.shared_password}\n\n` +
        `(no email was sent — DM yourself)`
      );
      await load();
    } catch (e) {
      window.alert(`Approve failed: ${e.message}`);
    } finally {
      setBusyEmail(null);
    }
  };

  const reject = async (email) => {
    const reason = window.prompt(`Reject ${email}?\n\nOptional reason (visible to admins only, not sent to applicant):`);
    if (reason === null) return; // user cancelled
    setBusyEmail(email);
    try {
      const r = await fetch(`${apiUrl}/api/admin/beta-applications/${encodeURIComponent(email)}/reject`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || r.statusText);
      await load();
    } catch (e) {
      window.alert(`Reject failed: ${e.message}`);
    } finally {
      setBusyEmail(null);
    }
  };

  const statusBadge = (s) => {
    if (s === 'approved') return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/40">approved</Badge>;
    if (s === 'rejected') return <Badge className="bg-red-500/20 text-red-400 border-red-500/40">rejected</Badge>;
    return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/40">pending</Badge>;
  };

  return (
    <Card className="bg-[#111111] border-[#27272A] rounded-sm" data-testid="admin-beta-panel">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-white font-['Outfit']">
              <ShieldCheck className="w-5 h-5 text-purple-400" />Beta Tester Applications
            </CardTitle>
            <CardDescription className="text-zinc-400">
              Approve manually. Each approval creates an account with shared password —
              DM it to the applicant out-of-band.
            </CardDescription>
          </div>
          <div className="flex gap-1 flex-wrap text-xs">
            <button
              onClick={() => setFilter('pending_review')}
              data-testid="filter-pending"
              className={`px-2.5 py-1 rounded-sm border ${filter === 'pending_review' ? 'border-amber-500 text-amber-400 bg-amber-500/10' : 'border-[#27272A] text-zinc-500 hover:text-zinc-300'}`}
            >
              <Clock className="w-3 h-3 inline mr-1" />Pending {counts.pending_review || 0}
            </button>
            <button
              onClick={() => setFilter('approved')}
              data-testid="filter-approved"
              className={`px-2.5 py-1 rounded-sm border ${filter === 'approved' ? 'border-emerald-500 text-emerald-400 bg-emerald-500/10' : 'border-[#27272A] text-zinc-500 hover:text-zinc-300'}`}
            >
              <CheckCircle2 className="w-3 h-3 inline mr-1" />Approved {counts.approved || 0}
            </button>
            <button
              onClick={() => setFilter('rejected')}
              data-testid="filter-rejected"
              className={`px-2.5 py-1 rounded-sm border ${filter === 'rejected' ? 'border-red-500 text-red-400 bg-red-500/10' : 'border-[#27272A] text-zinc-500 hover:text-zinc-300'}`}
            >
              <XCircle className="w-3 h-3 inline mr-1" />Rejected {counts.rejected || 0}
            </button>
            <button
              onClick={() => setFilter('')}
              data-testid="filter-all"
              className={`px-2.5 py-1 rounded-sm border ${filter === '' ? 'border-zinc-500 text-zinc-300 bg-zinc-500/10' : 'border-[#27272A] text-zinc-500 hover:text-zinc-300'}`}
            >
              All
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex items-center gap-2 text-zinc-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />Loading applications…
          </div>
        )}
        {error && (
          <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-sm p-3">
            {error}
          </div>
        )}
        {!loading && !error && apps.length === 0 && (
          <div className="text-zinc-500 text-sm italic py-4 text-center">
            No applications in this view.
          </div>
        )}
        {!loading && apps.length > 0 && (
          <div className="space-y-2">
            {apps.map((app) => (
              <div
                key={app.email}
                className="flex items-start justify-between gap-3 p-3 rounded-sm border border-[#27272A] bg-[#050505]"
                data-testid={`beta-app-${app.email}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Mail className="w-3.5 h-3.5 text-zinc-500" />
                    <span className="font-['JetBrains_Mono'] text-sm text-white truncate">{app.email}</span>
                    {statusBadge(app.status)}
                  </div>
                  {app.why && (
                    <p className="text-xs text-zinc-400 mt-1.5 leading-relaxed">{app.why}</p>
                  )}
                  <div className="flex gap-3 mt-1.5 text-[10px] text-zinc-600 font-['JetBrains_Mono']">
                    <span>applied {new Date(app.applied_at).toLocaleDateString()}</span>
                    {app.source && <span>via {app.source}</span>}
                    {app.approved_at && <span className="text-emerald-500">approved {new Date(app.approved_at).toLocaleDateString()}</span>}
                    {app.rejected_at && <span className="text-red-500">rejected {new Date(app.rejected_at).toLocaleDateString()}</span>}
                  </div>
                  {app.rejected_reason && (
                    <p className="text-[10px] text-red-400/70 italic mt-1">reason: {app.rejected_reason}</p>
                  )}
                </div>
                {app.status === 'pending_review' && (
                  <div className="flex gap-1.5 flex-shrink-0">
                    <Button
                      size="sm"
                      onClick={() => approve(app.email)}
                      disabled={busyEmail === app.email}
                      className="bg-emerald-500 hover:bg-emerald-600 text-black text-xs h-7 px-2.5 rounded-sm"
                      data-testid={`approve-${app.email}`}
                    >
                      {busyEmail === app.email ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Approve'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => reject(app.email)}
                      disabled={busyEmail === app.email}
                      className="border-red-500/40 text-red-400 hover:bg-red-500/10 text-xs h-7 px-2.5 rounded-sm"
                      data-testid={`reject-${app.email}`}
                    >
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
